import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { admin, errorMessage } from "../lib/api";
import type { Category, List } from "../lib/types";
import { Alert, Button, Field, Modal, PageHeader, Spinner, Table } from "../components/ui";
import SeoFields, { type Seo } from "../components/SeoFields";

type Form = { id?: string; name: string; handle: string; description: string; position: string; parent_id: string; seo: Seo };
const blank: Form = { name: "", handle: "", description: "", position: "0", parent_id: "", seo: {} };

export default function Categories() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["categories"], queryFn: () => admin.get<List<Category>>("/categories") });
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string>();

  const save = useMutation({
    mutationFn: (f: Form) => {
      const body = { name: f.name, description: f.description, position: Number(f.position) || 0, ...(f.handle ? { handle: f.handle } : {}),
        seo: { title: f.seo.title?.trim() || undefined, description: f.seo.description?.trim() || undefined },
        ...(f.parent_id ? { parent_id: f.parent_id } : {}) };
      return f.id ? admin.patch(`/categories/${f.id}`, body) : admin.post("/categories", body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["categories"] }); setForm(null); },
    onError: (e) => setError(errorMessage(e)),
  });
  const del = useMutation({
    mutationFn: (id: string) => admin.del(`/categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories"] }),
  });

  const cats = list.data?.data ?? [];
  const name = (id: string | null) => cats.find((c) => c.id === id)?.name;

  return (
    <>
      <PageHeader title="Categorii" subtitle="Structura de navigare a magazinului." actions={<Button onClick={() => { setError(undefined); setForm(blank); }}>+ Categorie</Button>} />
      {list.isLoading ? <Spinner /> : (
        <Table head={["Nume", "URL", "Părinte", "Poziție", ""]} empty={cats.length === 0}>
          {cats.map((c) => (
            <tr key={c.id} className="hover:bg-slate-50">
              <td className="px-4 py-3 font-semibold">{c.name}</td>
              <td className="px-4 py-3 font-mono text-xs text-slate-500">/c/{c.handle}</td>
              <td className="px-4 py-3 text-slate-500">{name(c.parent_id) ?? "—"}</td>
              <td className="px-4 py-3">{c.position}</td>
              <td className="px-4 py-3 text-right">
                <Button size="sm" variant="ghost" onClick={() => { setError(undefined); setForm({ id: c.id, name: c.name, handle: c.handle, description: c.description, position: String(c.position), parent_id: c.parent_id ?? "", seo: c.seo ?? {} }); }}>Editează</Button>
                <Button size="sm" variant="ghost" onClick={() => confirm(`Ștergi categoria „${c.name}”?`) && del.mutate(c.id)}>Șterge</Button>
              </td>
            </tr>
          ))}
        </Table>
      )}
      <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? "Editează categoria" : "Categorie nouă"}>
        {form && (
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(form); }}>
            <Field label="Nume"><input className="input" required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="URL (handle)" hint="Gol = generat automat"><input className="input font-mono text-xs" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} /></Field>
            <Field label="Categorie părinte">
              <select className="input" value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })}>
                <option value="">— niciuna —</option>
                {cats.filter((c) => c.id !== form.id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Descriere"><textarea className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            <details className="rounded-lg border border-slate-200 p-3">
              <summary className="cursor-pointer text-xs font-semibold text-slate-600">SEO (motoare de căutare)</summary>
              <div className="mt-3">
                <SeoFields value={form.seo} onChange={(seo) => setForm({ ...form, seo })} fallbackTitle={form.name || "Numele categoriei"}
                  fallbackDescription={`${form.name || "Categoria"}: vezi produsele disponibile, prețurile și livrarea.`} path={`/c/${form.handle || "…"}`} />
              </div>
            </details>
            <Field label="Poziție"><input className="input w-24" inputMode="numeric" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} /></Field>
            {error && <Alert>{error}</Alert>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setForm(null)}>Anulează</Button>
              <Button disabled={save.isPending}>Salvează</Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
